import React, { useRef, useState, useEffect } from 'react';
import { DataService } from '../../services/DataService';
import { Modal } from '../shared/Modal';
import { soundService } from '../../services/SoundService';
import { useToast } from '../../contexts/ToastContext';
import { GameState } from '../../domain/entities';
import { getAutosave, SaveFile } from '../../infrastructure/GameStateRepository';
import { GuideScreen } from './GuideScreen';
import './StartScreen.css';
import { GAME_VERSION_NUMBER } from '../../version';
import { PitchBackground } from '../shared/PitchBackground';
import { AdPlaceholder } from '../shared/AdPlaceholder';

interface StartScreenProps {
    onStart: () => void;
    onNewGame: (data: any | null, gameType: 'mens' | 'womens') => void;
    hasSave: boolean;
    onLoadGameState: (gameState: GameState) => void;
    listSaves: () => Promise<SaveFile[]>;
    onLoadNamedGame: (id: number) => Promise<boolean>;
    onDeleteNamedGame: (id: number) => Promise<void>;
    onShowChallenges: () => void;
    migrationError: string | null;
    adsEnabled: boolean;
}

export const StartScreen = ({ onStart, onNewGame, hasSave, onLoadGameState, listSaves, onLoadNamedGame, onDeleteNamedGame, onShowChallenges, migrationError, adsEnabled = true }: StartScreenProps) => {
    const customDataInputRef = useRef<HTMLInputElement>(null);
    const loadGameInputRef = useRef<HTMLInputElement>(null);
    const [newGameModalOpen, setNewGameModalOpen] = useState(false);
    const [gameTypeModalOpen, setGameTypeModalOpen] = useState(false);
    const [optionsModalOpen, setOptionsModalOpen] = useState(false);
    const [saveDetails, setSaveDetails] = useState<GameState | null>(null);
    const [savesModalOpen, setSavesModalOpen] = useState(false);
    const [saves, setSaves] = useState<SaveFile[]>([]);
    const [showGuide, setShowGuide] = useState(false);

    const { addToast } = useToast();
    const title = "Pixel Soccer Manager";

    useEffect(() => {
        if (hasSave) {
            (async () => {
                const savedGame = await getAutosave();
                if (savedGame && savedGame.gameStarted) {
                    setSaveDetails(savedGame);
                }
            })();
        }
    }, [hasSave]);

    useEffect(() => {
        if (migrationError) {
            addToast(migrationError, 'error');
        }
    }, [migrationError, addToast]);

    const handleContinue = () => {
        soundService.playClick();
        onStart();
    }

    const handleNewGameClick = () => {
        soundService.playClick();
        if (hasSave) {
            setNewGameModalOpen(true);
        } else {
            setGameTypeModalOpen(true);
        }
    }
    
    const handleConfirmNewGame = () => {
        soundService.playSuccess();
        setNewGameModalOpen(false);
        setGameTypeModalOpen(true);
    }

    const handleSelectGameType = (gameType: 'mens' | 'womens') => {
        soundService.playSuccess();
        setGameTypeModalOpen(false);
        onNewGame(null, gameType);
    };

    const handleLoadFromFile = () => {
        soundService.playClick();
        loadGameInputRef.current?.click();
    };

    const handleLoadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            DataService.importFullGameState(file)
                .then(gameState => {
                    soundService.playSuccess();
                    addToast('Game state loaded successfully!', 'success');
                    onLoadGameState(gameState);
                })
                .catch(error => {
                    soundService.playError();
                    addToast(`Load failed: ${error.message}`, 'error');
                });
        }
        if (loadGameInputRef.current) {
            loadGameInputRef.current.value = '';
        }
    };

    const handleImportCustomData = () => {
        soundService.playClick();
        customDataInputRef.current?.click();
    };

    const handleCustomDataFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            DataService.importData(file)
                .then(data => {
                    soundService.playSuccess();
                    addToast('Custom data imported! Starting new game.', 'success');
                    onNewGame(data, data.gameType || 'mens');
                })
                .catch(error => {
                    soundService.playError();
                    addToast(`Import failed: ${error.message}`, 'error');
                });
        }
        if (customDataInputRef.current) {
            customDataInputRef.current.value = '';
        }
    };

    const handleExportBaseData = (gameType: 'mens' | 'womens') => {
        soundService.playClick();
        DataService.exportBaseData(gameType);
        addToast(`${gameType === 'mens' ? "Men's" : "Women's"} base data exported!`, 'info');
    };

    const handleManageSaves = async () => {
        soundService.playClick();
        const savesList = await listSaves();
        setSaves(savesList);
        setSavesModalOpen(true);
    };

    const handleLoadGame = async (id: number) => {
        soundService.playClick();
        const success = await onLoadNamedGame(id);
        if (success) {
            addToast('Game loaded successfully!', 'success');
        } else {
            addToast('Failed to load game.', 'error');
        }
        setSavesModalOpen(false);
    };

    const handleDeleteGame = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        soundService.playClick();
        if (window.confirm('Are you sure you want to delete this save?')) {
            await onDeleteNamedGame(id);
            addToast('Save deleted!', 'info');
            const savesList = await listSaves();
            setSaves(savesList);
        }
    };

    if (showGuide) {
        return (
             <div className="start-screen-container">
                <div className="start-screen-pitch-bg">
                    <PitchBackground 
                        randomizeOnMount={true} 
                        style={{ boxShadow: 'none' }}
                    />
                </div>
                <GuideScreen onBack={() => { soundService.playClick(); setShowGuide(false); }} />
            </div>
        );
    }

    return (
        <div className="start-screen-container">
            {/* Ad Section */}
            {adsEnabled && (
                <div className="start-screen-ad-section">
                    <AdPlaceholder format="horizontal" testMode={false} forceShow={true} />
                </div>
            )}            
            
            <div className="start-screen-pitch-bg">
                <PitchBackground 
                    randomizeOnMount={true} 
                    style={{ boxShadow: 'none' }}
                />
            </div>
            <div className="start-screen-main-panel">
                <h1 className="title" data-text={title}>{title}</h1>
                <p className="subtitle">A roguelike soccer management game.</p>
                
                {hasSave && saveDetails && (
                    <div className="save-info-display">
                        <h3>Current Game</h3>
                        <div className="save-info-grid">
                            <span>Team:</span> <strong>{saveDetails.playerTeamName}</strong>
                            <span>League:</span> <strong>{saveDetails.playerLeagueName}</strong>
                            <span>Season:</span> <strong>{saveDetails.season}</strong>
                            <span>Week:</span> <strong>{Math.floor(saveDetails.currentWeek)}</strong>
                            <span>Mode:</span> <strong>{saveDetails.gameType === 'womens' ? "Women's" : "Men's"}</strong>
                        </div>
                    </div>
                )}

                <div className="start-screen-actions">
                    {hasSave && (
                        <button className="button primary" onClick={handleContinue}>Continue Game</button>
                    )}
                    <button className="button" onClick={handleNewGameClick}>New Game</button>
                    <button className="button secondary" onClick={() => { soundService.playClick(); onShowChallenges(); }}>Challenges</button>
                    <button className="button button-guide" onClick={() => { soundService.playClick(); setShowGuide(true); }}>Help</button>
                    <div className="start-screen-sub-actions">
                        <button className="button secondary" onClick={handleManageSaves}>Manage Saves</button>
                        <button className="button secondary" onClick={() => setOptionsModalOpen(true)}>Options</button>
                    </div>
                </div>
                <p className="version-info">v{GAME_VERSION_NUMBER}</p>
            </div>

            <Modal isOpen={newGameModalOpen} onClose={() => setNewGameModalOpen(false)}>
                <h2>Start New Game?</h2>
                <p className="negative">This will overwrite your existing saved game. Are you sure you want to continue?</p>
                <div className="modal-actions">
                    <button className="button" onClick={() => setNewGameModalOpen(false)}>Cancel</button>
                    <button className="button danger" onClick={handleConfirmNewGame}>Confirm</button>
                </div>
            </Modal>

            <Modal isOpen={gameTypeModalOpen} onClose={() => setGameTypeModalOpen(false)}>
                <h2>Choose Game Type</h2>
                <p>Select which football world you want to manage in.</p>
                <div className="game-type-selection">
                    <button className="button" onClick={() => handleSelectGameType('mens')}>
                        Men's
                        <br />
                        Football
                    </button>
                    <button className="button" onClick={() => handleSelectGameType('womens')}>
                        Women's
                        <br />
                        Football
                    </button>
                </div>
            </Modal>
            
            <Modal isOpen={optionsModalOpen} onClose={() => setOptionsModalOpen(false)}>
                <h2>Options</h2>
                <div className="options-modal-content">
                    <button className="button" onClick={handleLoadFromFile}>Load Game from File</button>
                    <button className="button" onClick={handleImportCustomData}>Import Custom Data & New Game</button>
                    <button className="button" onClick={() => handleExportBaseData('mens')}>Export Men's Base Data</button>
                    <button className="button" onClick={() => handleExportBaseData('womens')}>Export Women's Base Data</button>
                </div>
                <div className="modal-actions">
                    <button className="button" onClick={() => setOptionsModalOpen(false)}>Close</button>
                </div>
            </Modal>

             <Modal isOpen={savesModalOpen} onClose={() => setSavesModalOpen(false)}>
                <h2>Manage Saves</h2>
                <div className="table-wrapper">
                    {saves.length > 0 ? (
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Save</th>
                                    <th>Details</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {saves.map(save => (
                                    <tr key={save.id} className="clickable-row" onClick={() => handleLoadGame(save.id)}>
                                        <td>{save.name}<br /><small>{new Date(save.timestamp).toLocaleString()}</small></td>
                                        <td>{save.playerTeamName}<br />S{save.season} W{save.currentWeek}</td>
                                        <td>
                                            <button className="button small danger" onClick={(e) => handleDeleteGame(e, save.id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <p>No named saves found.</p>}
                </div>
                <div className="modal-actions">
                    <button className="button" onClick={() => setSavesModalOpen(false)}>Cancel</button>
                </div>
            </Modal>

            <input type="file" ref={customDataInputRef} onChange={handleCustomDataFileChange} style={{ display: 'none' }} accept=".json,.json.gz,.gz,.zip" />
            <input type="file" ref={loadGameInputRef} onChange={handleLoadFileChange} style={{ display: 'none' }} accept=".json,.json.gz,.gz,.zip" />
        </div>
    );
};